import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import { openDecagonChat } from 'components/Decagon';
import { Dropdown } from 'components/Dropdown';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { openModal } from 'components/Modal';
import Roster, { rosterMessages, SORT_OPTION, SortMenu } from 'components/Roster';
import { PFE_ICONS } from 'constants/icons';
import { schedulePropTypes } from 'constants/propTypes';
import URLS from 'constants/urls';
import { bem } from 'helpers/class-name';
import { reloadPage } from 'helpers/page-utils';
import { selectActiveVenue } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';

import EmptyRoster from '../EmptyRoster';
import RosterListItem from '../RosterListItem';
import { emailMember, updateReservationAttendance } from '../YieldManagement.saga';
import AttendanceCard from './AttendanceCard';
import messages from './RosterTabContentV2.messages';
import { getAttendancePhase, getRosterAttendanceWindow } from './ScheduleDetailV2.helpers';
import { useScheduleRoster } from './ScheduleDetailV2.hooks';
import detailMessages from './ScheduleDetailV2.messages';
import './RosterTabContentV2.scss';

const { element } = bem('roster-tab-v2');

const ROSTER_SORT_MENU_ID = 'roster-list-item__sort-menu';
const ROSTER_SORT_OPTIONS = [SORT_OPTION.ALPHABETIC, SORT_OPTION.VISITS, SORT_OPTION.RECENT];

export function RosterTabActions({ onSortSelect, schedule, sort }) {
  const intl = useIntl();
  const { venueId } = useSelector(selectActiveVenue);
  const { data: roster, isSuccess } = useScheduleRoster(schedule.scheduleId);
  const canPrint = isSuccess && roster?.length > 0;
  const printLabel = intl.formatMessage(rosterMessages.ariaLabelPrintRoster);
  const printClassName = element('print-link', { disabled: !canPrint });
  const printIcon = <Isvg className={element('icon')} src={PFE_ICONS.printer} />;

  return (
    <div className={element('actions')} data-cypress="roster-v2-actions">
      <Dropdown
        Toggle={({ isOpen, toggleMenu }) => (
          <button
            type="button"
            aria-label={intl.formatMessage(rosterMessages.ariaLabelSortRoster)}
            aria-haspopup
            aria-expanded={isOpen}
            aria-controls={ROSTER_SORT_MENU_ID}
            onClick={toggleMenu}
            className={element('icon-button')}>
            <Isvg className={element('icon')} src={PFE_ICONS.filterSmall} />
          </button>
        )}
        Content={props => (
          <SortMenu
            title={intl.formatMessage(rosterMessages.sortMenuTitle)}
            onSelect={onSortSelect}
            options={ROSTER_SORT_OPTIONS}
            sort={sort}
            id={ROSTER_SORT_MENU_ID}
            {...props}
          />
        )}
      />
      {canPrint ? (
        <a
          aria-label={printLabel}
          className={printClassName}
          href={URLS.schedule.print(venueId, schedule.scheduleId, schedule.startDate)}
          target="_blank"
          rel="noopener noreferrer">
          {printIcon}
        </a>
      ) : (
        <button type="button" aria-label={printLabel} className={printClassName} disabled>
          {printIcon}
        </button>
      )}
    </div>
  );
}

RosterTabActions.propTypes = {
  onSortSelect: PropTypes.func.isRequired,
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
  sort: PropTypes.string.isRequired,
};

const RosterMobileHeader = ({ onSortSelect, schedule, sort }) => (
  <div className={element('mobile-header')}>
    <Message as="h3" className={element('title')} message={detailMessages.tabRoster} />
    <RosterTabActions onSortSelect={onSortSelect} schedule={schedule} sort={sort} />
  </div>
);

RosterMobileHeader.propTypes = {
  onSortSelect: PropTypes.func.isRequired,
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
  sort: PropTypes.string.isRequired,
};

const RosterTabContentV2 = ({ onSortSelect, schedule, sort }) => {
  const dispatch = useDispatch();
  const isDesktop = useSelector(selectIsDesktop);
  const { venueName, venueSubtitle } = useSelector(selectActiveVenue);
  const { data: roster, isError, isLoading } = useScheduleRoster(schedule.scheduleId);
  const partnerSuccessLink = chunks => (
    <Button className={element('support-link')} onClick={openDecagonChat} styled="inline-link">
      {chunks}
    </Button>
  );

  let body;
  if (isError) {
    body = (
      <div className={element('status')} data-cypress="roster-v2-error">
        <div className={element('error')}>
          <Message as="p" className={element('title')} message={messages.unableToLoadRoster} />
          <p className={element('body')}>
            <Message message={messages.unableToLoadRosterHint} />
            <br />
            <Message message={messages.unableToLoadRosterSupport} values={{ a: partnerSuccessLink }} />
          </p>
          <Message
            as={Button}
            className={element('refresh')}
            message={messages.refreshPage}
            onClick={reloadPage}
            styled="primary-outline"
          />
        </div>
      </div>
    );
  } else if (isLoading) {
    body = (
      <div className={element('status')} data-cypress="roster-v2-loading">
        <LoadingDots />
      </div>
    );
  } else {
    const attendanceWindow = getRosterAttendanceWindow(schedule.startDateTime);
    const rosterWithSchedule = (roster || []).map(reservation => ({ ...reservation, schedule }));

    body = (
      <>
        {rosterWithSchedule.length > 0 && (
          <AttendanceCard
            phase={getAttendancePhase(attendanceWindow)}
            roster={rosterWithSchedule}
            scheduleId={schedule.scheduleId}
          />
        )}
        <Roster
          className={element('list')}
          RowComponent={RosterListItem}
          getKey={item => item.reservationId}
          data={rosterWithSchedule}
          EmptyComponent={EmptyRoster}
          sort={sort}
          canUpdateAttendance={attendanceWindow.canUpdateAttendance}
          isScheduleAfterUpdateAttendanceTimeRange={attendanceWindow.isScheduleAfterUpdateAttendanceTimeRange}
          isScheduleBeforeUpdateAttendanceTimeRange={attendanceWindow.isScheduleBeforeUpdateAttendanceTimeRange}
          updateReservationAttendance={(reservationId, attended) =>
            dispatch(updateReservationAttendance(schedule.scheduleId, reservationId, attended))
          }
          emailMember={(...args) => dispatch(emailMember(...args))}
          openModal={(...args) => dispatch(openModal(...args))}
          venueName={venueName}
          venueSubtitle={venueSubtitle}
          noScroll
        />
      </>
    );
  }

  return (
    <>
      {!isDesktop && <RosterMobileHeader onSortSelect={onSortSelect} schedule={schedule} sort={sort} />}
      {body}
    </>
  );
};

RosterTabContentV2.propTypes = {
  onSortSelect: PropTypes.func.isRequired,
  schedule: PropTypes.shape(schedulePropTypes),
  sort: PropTypes.string.isRequired,
};

export default RosterTabContentV2;
